import { isXmlMediaType } from '@scalar/helpers/http/is-xml-media-type';
import { getRaw } from '@scalar/json-magic/magic-proxy';
import { anchor, createDocumentAnchors, describe, heading, inlineCode, item, link, list, paragraph, strong, text, } from '@scalar/openapi-to-markdown/markdown';
import { resolveTraits } from '@scalar/workspace-store/helpers/resolve-traits';
import { getXmlBodyExample } from '@scalar/workspace-store/request-example';
import { getBindingSchemas } from './binding-schemas.js';
import { object, string } from './document.js';
import { createExampleRenderer } from './render-examples.js';
import { code, createSchemaRenderer } from './render-schema.js';
const deeper = (depth) => Math.min(depth + 1, 6);
const title = (value, fallback) => string(value.title) || string(value.summary) || string(value.name) || fallback;
const entries = (value) => Object.entries(object(value));
const withTraits = (value) => {
    const target = object(value);
    return resolveTraits(target, Array.isArray(target.traits) ? target.traits.map(object) : []);
};
/** Build the sections of one AsyncAPI page without retaining mutable state between calls. */
export const createContentRenderer = (document, references, view, linked, selectedModel, selectedTag, inlinePrimitives = true) => {
    const examples = createExampleRenderer(inlinePrimitives);
    const renderSchema = createSchemaRenderer(references.reference, view, linked);
    const schema = (input, expand = false, mediaType = 'application/json') => expand
        ? renderSchema(input, true, mediaType)
        : references.root(input, () => renderSchema(input, false, mediaType), mediaType);
    const anchors = createDocumentAnchors();
    const shownMessages = new Map();
    const shownServers = new Map();
    const shownSecurity = new Map();
    const blocks = [];
    const metadata = (value, includeTagDocumentation = true) => {
        if (value.summary && value.summary !== value.title) {
            blocks.push(...describe(value.summary));
        }
        blocks.push(...describe(value.description));
        const tags = Array.isArray(value.tags) ? value.tags.map((tag) => string(object(tag).name)).filter(Boolean) : [];
        const visibleTags = selectedTag ? tags.filter((tag) => tag === selectedTag) : tags;
        if (visibleTags.length) {
            blocks.push(paragraph(strong(text('Tags:')), text(' '), text(visibleTags.join(', '))));
        }
        if (includeTagDocumentation && Array.isArray(value.tags)) {
            for (const entry of value.tags) {
                const tag = object(entry);
                if (selectedTag && tag.name !== selectedTag) {
                    continue;
                }
                const external = object(tag.externalDocs);
                if (tag.description || typeof external.url === 'string') {
                    blocks.push(paragraph(strong(text(`Tag: ${string(tag.name) ?? ''}`))), ...describe(tag.description));
                    if (typeof external.url === 'string') {
                        blocks.push(paragraph(link(external.url, string(external.description) || 'External documentation')));
                    }
                }
            }
        }
        const external = object(value.externalDocs);
        if (typeof external.url === 'string') {
            blocks.push(paragraph(link(external.url, string(external.description) || 'External documentation')));
        }
    };
    const fields = (value, names) => {
        const items = names.flatMap((name) => value[name] === undefined
            ? []
            : [
                item(paragraph(strong(text(`${name}:`)), text(' '), inlineCode(typeof value[name] === 'string' ? value[name] : JSON.stringify(getRaw(value[name]))))),
            ]);
        if (items.length) {
            blocks.push(list(items));
        }
    };
    const bindings = (value, depth) => {
        const protocols = entries(value);
        if (!protocols.length) {
            return;
        }
        blocks.push(heading(depth, text('Bindings')));
        for (const [protocol, binding] of protocols) {
            const resolved = object(binding);
            blocks.push(paragraph(strong(text(protocol))), code(resolved));
            for (const reference of getBindingSchemas(resolved)) {
                blocks.push(paragraph(strong(text('Referenced schema:')), text(' '), inlineCode(reference.$ref)), ...schema(reference));
            }
        }
    };
    const security = (value, depth, operationSecurity = false) => {
        if (!Array.isArray(value) || !value.length) {
            return;
        }
        blocks.push(heading(depth, text('Authentication')), paragraph(text('Satisfy one of the following security schemes.')));
        if (operationSecurity) {
            blocks.push(paragraph(text('Applicable server security must also be satisfied.')));
        }
        for (const entry of value) {
            const scheme = object(entry);
            const identity = getRaw(scheme);
            const previous = shownSecurity.get(identity);
            const label = string(scheme.name) || string(scheme.type) || 'Security scheme';
            if (previous) {
                blocks.push(paragraph(link(`#${previous}`, label)));
                continue;
            }
            const id = anchors.get('security', String(shownSecurity.size), label);
            shownSecurity.set(identity, id);
            blocks.push(anchor(id));
            fields(scheme, ['type', 'name', 'in', 'scheme', 'bearerFormat', 'openIdConnectUrl', 'scopes']);
            blocks.push(...describe(scheme.description));
            if (scheme.flows) {
                blocks.push(code(scheme.flows));
            }
        }
    };
    const servers = (value, depth) => {
        if (!value.length) {
            return;
        }
        blocks.push(heading(depth, text('Servers')));
        for (const [name, entry] of value) {
            const server = object(entry);
            const identity = getRaw(server);
            const previous = shownServers.get(identity);
            if (previous) {
                blocks.push(paragraph(link(`#${previous}`, title(server, name))));
                continue;
            }
            const serverId = anchors.get('server', String(shownServers.size), name);
            shownServers.set(identity, serverId);
            blocks.push(anchor(serverId));
            blocks.push(heading(deeper(depth), text(title(server, name))));
            fields(server, ['host', 'pathname', 'protocol', 'protocolVersion']);
            metadata(server);
            for (const [variableName, variable] of entries(server.variables)) {
                blocks.push(paragraph(strong(text('Server variable:')), text(' '), inlineCode(variableName)));
                fields(object(variable), ['default', 'enum', 'examples']);
                blocks.push(...describe(object(variable).description));
            }
            security(server.security, deeper(deeper(depth)));
            bindings(server.bindings, deeper(deeper(depth)));
        }
    };
    const channelServers = (channel) => Array.isArray(channel.servers) && channel.servers.length > 0
        ? channel.servers.map((server, index) => [string(object(server).title) || `Server ${index + 1}`, server])
        : entries(document.servers);
    const message = (name, value, depth) => {
        const identity = getRaw(object(value));
        if (selectedTag) {
            const previous = shownMessages.get(identity);
            if (previous) {
                blocks.push(paragraph(link(`#${previous}`, title(withTraits(value), name))));
                return;
            }
            const id = anchors.get('message', String(shownMessages.size + 1), name);
            shownMessages.set(identity, id);
            blocks.push(anchor(id));
        }
        const msg = withTraits(value);
        const contentType = string(msg.contentType ?? document.defaultContentType) ?? 'application/json';
        blocks.push(heading(depth, text(title(msg, name))));
        metadata(msg);
        fields({ ...msg, contentType: msg.contentType ?? document.defaultContentType }, ['name', 'contentType']);
        if (msg.correlationId) {
            blocks.push(heading(deeper(depth), text('Correlation ID')));
            fields(object(msg.correlationId), ['location']);
            metadata(object(msg.correlationId));
        }
        for (const key of ['headers', 'payload']) {
            if (msg[key] !== undefined) {
                blocks.push(heading(deeper(depth), text(key === 'headers' ? 'Headers' : 'Payload')), ...schema(key === 'payload' && typeof msg.schemaFormat === 'string'
                    ? { schemaFormat: msg.schemaFormat, schema: msg[key] }
                    : msg[key], false, key === 'payload' ? contentType : 'application/json'));
            }
        }
        if (Array.isArray(msg.examples)) {
            for (const example of msg.examples) {
                const authored = object(example);
                blocks.push(heading(deeper(depth), text(string(authored.name) || 'Example')));
                blocks.push(...describe(authored.summary));
                // Message examples contain authored headers and payload, not generated values.
                for (const key of ['headers', 'payload']) {
                    if (authored[key] !== undefined) {
                        const xml = key === 'payload' && isXmlMediaType(contentType);
                        const result = xml && typeof authored[key] !== 'string'
                            ? getXmlBodyExample(object(msg.payload), { value: authored[key] })
                            : undefined;
                        blocks.push(paragraph(strong(text(`${key}:`))));
                        blocks.push(xml
                            ? typeof authored[key] === 'string' || result?.xml !== undefined
                                ? code(result?.xml ?? authored[key], 'xml')
                                : paragraph(text('Unable to generate an XML example.'))
                            : code(authored[key]));
                    }
                }
            }
        }
        if (!Array.isArray(msg.examples) || !msg.examples.length) {
            for (const key of ['headers', 'payload']) {
                if (msg[key] !== undefined) {
                    blocks.push(...examples(key === 'payload' && typeof msg.schemaFormat === 'string'
                        ? { schemaFormat: msg.schemaFormat, schema: msg[key] }
                        : msg[key], linked, false, key === 'payload' ? contentType : 'application/json', key));
                }
            }
        }
        bindings(msg.bindings, deeper(depth));
    };
    const channelContext = (channel, depth) => {
        if (channel.address === undefined || channel.address === null) {
            blocks.push(paragraph(strong(text('Address:')), text(' Unknown.')));
        }
        else {
            fields(channel, ['address']);
        }
        metadata(channel);
        for (const [name, parameter] of entries(channel.parameters)) {
            blocks.push(heading(depth, text(`Parameter: ${name}`)));
            fields(object(parameter), ['enum', 'default', 'examples', 'location']);
            metadata(object(parameter));
        }
        servers(channelServers(channel), depth);
        bindings(channel.bindings, depth);
    };
    const messages = (owner, channel) => Array.isArray(owner.messages)
        ? owner.messages.map((msg, index) => [title(object(msg), `Message ${index + 1}`), msg])
        : entries(channel.messages);
    const operation = (name, value, depth, includeChannel) => {
        const op = withTraits(value);
        const channel = object(op.channel);
        blocks.push(heading(depth, text(title(op, name))));
        fields(op, ['action']);
        metadata(op);
        if (includeChannel) {
            blocks.push(heading(deeper(depth), text('Channel')));
            channelContext(channel, deeper(deeper(depth)));
        }
        security(op.security, deeper(depth), true);
        bindings(op.bindings, deeper(depth));
        for (const [messageName, msg] of messages(op, channel)) {
            message(messageName, msg, deeper(depth));
        }
        if (op.reply) {
            const reply = object(op.reply);
            const replyChannel = object(reply.channel);
            blocks.push(heading(deeper(depth), text('Reply')));
            if (reply.channel !== undefined) {
                channelContext(replyChannel, deeper(deeper(depth)));
            }
            const address = object(reply.address);
            fields(address, ['location']);
            metadata(address);
            for (const [messageName, msg] of messages(reply, {})) {
                message(messageName, msg, deeper(deeper(depth)));
            }
        }
    };
    const channel = (name, value, depth) => {
        const current = object(value);
        blocks.push(heading(depth, text(title(current, name))));
        channelContext(current, deeper(depth));
        const operations = entries(document.operations).filter(([, op]) => getRaw(object(withTraits(op).channel)) === getRaw(current));
        for (const [operationName, op] of operations) {
            operation(operationName, op, deeper(depth), false);
        }
        // Include channel messages even when no operation uses them.
        const shown = new Set(operations.flatMap(([, op]) => messages(withTraits(op), current).map(([, msg]) => getRaw(object(msg)))));
        for (const [messageName, msg] of entries(current.messages)) {
            if (!shown.has(getRaw(object(msg)))) {
                message(messageName, msg, deeper(depth));
            }
        }
    };
    const introduction = () => {
        const info = object(document.info);
        blocks.push(heading(1, text(title(info, 'AsyncAPI'))));
        fields({ version: info.version, asyncapi: document['x-original-aas-version'] ?? document.asyncapi }, [
            'version',
            'asyncapi',
        ]);
        fields(document, ['id', 'defaultContentType']);
        metadata(info, false);
        fields(info, ['termsOfService']);
        for (const name of ['contact', 'license']) {
            if (info[name]) {
                blocks.push(heading(2, text(name === 'contact' ? 'Contact' : 'License')));
                fields(object(info[name]), ['name', 'email', 'url']);
            }
        }
        servers(entries(document.servers), 2);
    };
    const model = (name, value, depth) => {
        blocks.push(...references.model(value, () => [
            heading(depth, text(title(object(value), name))),
            ...schema(value, true),
            ...(view(object(value)).type === 'object' || view(object(value)).properties !== undefined
                ? examples(value, linked && !selectedModel, linked && selectedModel)
                : []),
        ]));
    };
    return {
        blocks,
        introduction,
        channel,
        operation,
        message,
        model,
        finish: () => {
            blocks.push(...references.appendix((value) => schema(value, true)));
        },
    };
};
